"use client";

import { useState } from "react";
import { changeMyPassword, resetUserPassword } from "@/lib/api";

type Props = {
  mode: "self" | "admin";
  userId?: number;
  userName?: string;
  onClose: () => void;
  onSuccess: () => void;
};

export default function PasswordChangeModal({
  mode,
  userId,
  userName,
  onClose,
  onSuccess,
}: Props) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (newPassword.length < 4) {
      setError("새 비밀번호는 4글자 이상이어야 합니다.");
      return;
    }
    if (new TextEncoder().encode(newPassword).length > 72) {
      setError("새 비밀번호는 영문 기준 72자 이내로 입력해 주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    if (mode === "self" && !currentPassword) {
      setError("현재 비밀번호를 입력해 주세요.");
      return;
    }
    if (mode === "admin" && userId == null) {
      setError("초기화할 계정을 찾을 수 없습니다.");
      return;
    }

    setLoading(true);
    try {
      const result = mode === "self"
        ? await changeMyPassword({
            current_password: currentPassword,
            new_password: newPassword,
            new_password_confirm: confirmPassword,
          })
        : await resetUserPassword(userId!, {
            new_password: newPassword,
            new_password_confirm: confirmPassword,
          });
      alert(result.message);
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "비밀번호 변경에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }

  const isSelf = mode === "self";
  const title = isSelf ? "내 비밀번호 변경" : `${userName ?? "직원"} 비밀번호 초기화`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={() => { if (!loading) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-modal-title"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="password-modal-title" className="text-lg font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isSelf && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">현재 비밀번호</label>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                required
                autoFocus
              />
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">새 비밀번호</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              minLength={4}
              required
              autoFocus={!isSelf}
            />
            <p className="mt-1 text-xs text-gray-400">4글자 이상, UTF-8 기준 72바이트 이하로 입력하세요.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">새 비밀번호 확인</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              minLength={4}
              required
            />
          </div>

          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={loading} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              취소
            </button>
            <button type="submit" disabled={loading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300">
              {loading ? "처리 중..." : isSelf ? "변경" : "초기화"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
